import categoryList from "@/mocks/categoryList.json";

type LearnLink = { name: string; path: string };
type LearnCategory = {
  categoryName: string;
  title: string;
  parentLink: string;
  links: LearnLink[];
};

/**
 * Per-Learn-slug overrides where the curated topic name in
 * categoryList.json diverges from the canonical Algolia
 * `skillsData.skill` facet value on rv_product_summary. Without these,
 * the dropdown would silently no-op on high-traffic topics: e.g.
 * /learn/python sees the topic name "Python" but Algolia stores it as
 * "Python (Programming Language)".
 *
 * Captured from a live facet probe against rv_product_summary. Add a
 * new entry whenever a topic name in categoryList.json is found to
 * differ from the live facet label.
 */
const LEARN_SLUG_TO_ALGOLIA_SKILL: Record<string, string> = {
  python: "Python (Programming Language)",
  java: "Java (Programming Language)",
  javascript: "JavaScript (Programming Language)",
  "c-programming": "C (Programming Language)",
  django: "Django (Web Framework)",
  azure: "Microsoft Azure",
  html: "HyperText Markup Language (HTML)",
  english: "English Language",
  italian: "Italian Language",
  spanish: "Spanish Language",
  chinese: "Mandarin Chinese",
  mandarin: "Mandarin Chinese",
};

/**
 * Learn slugs whose categoryList topic has no representative skill in
 * the live Algolia facet. Listed explicitly so the no-preselect intent
 * is discoverable in code rather than relying on the dropdown's silent
 * fallback.
 */
const LEARN_SLUGS_WITHOUT_ALGOLIA_SKILL = new Set<string>([
  "esl",
  "japanese",
  "sign-language",
  "art",
  "fashion",
  "history",
  "literature",
  "shakespeare",
  "epidemics",
  "human-anatomy",
]);

/**
 * Curated representative skill per Learn hub page. Hub pages cover an
 * entire subject so we pick an editorially recognizable skill from the
 * hub's top facet values rather than the literal #1 by product count,
 * which skews toward generic skills like "Management" and "Innovation".
 * Values verified against the live Algolia facet.
 */
const HUB_PATH_TO_REPRESENTATIVE_SKILL: Record<string, string> = {
  "/learn/computer-science": "Python (Programming Language)",
  "/learn/data-science": "Data Analysis",
  "/learn/business-management": "Leadership",
  "/learn/engineering": "Software Engineering",
  "/learn/humanities": "Writing",
  "/learn/language": "Grammar",
};

const trimTrailingSlash = (path: string): string =>
  path.replace(/\/+$/, "") || "/";

const buildPathToSkillMap = (): Map<string, string> => {
  const map = new Map<string, string>();

  for (const category of categoryList as LearnCategory[]) {
    if (category.parentLink?.startsWith("/learn/")) {
      const hubPath = trimTrailingSlash(category.parentLink);
      const hubSkill = HUB_PATH_TO_REPRESENTATIVE_SKILL[hubPath];
      if (hubSkill && !map.has(hubPath)) {
        map.set(hubPath, hubSkill);
      }
    }

    for (const link of category.links) {
      if (!link.path?.startsWith("/learn/")) continue;
      const path = trimTrailingSlash(link.path);
      if (map.has(path)) continue;

      const slug = path.replace(/^\/learn\//, "");
      if (LEARN_SLUGS_WITHOUT_ALGOLIA_SKILL.has(slug)) continue;

      const override = LEARN_SLUG_TO_ALGOLIA_SKILL[slug];
      if (override) {
        map.set(path, override);
        continue;
      }

      if (link.name) {
        map.set(path, link.name);
      }
    }
  }

  return map;
};

const PATH_TO_SKILL = buildPathToSkillMap();

const stripLocalePrefix = (pathname: string): string => {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length >= 2 && parts[1] === "learn") {
    return "/" + parts.slice(1).join("/");
  }
  return "/" + parts.join("/");
};

/**
 * Resolve a candidate skill for a given Learn pathname so the Get
 * Started With component can pre-populate the Skill filter alongside
 * the page-derived Subject.
 *
 * Returns the Algolia skill facet value (e.g. "Python (Programming
 * Language)", "Leadership", "Data Analysis") or `undefined` when:
 *   - the pathname is outside the Learn taxonomy
 *   - the slug has no representative skill in the live Algolia facet
 *     (listed explicitly in LEARN_SLUGS_WITHOUT_ALGOLIA_SKILL)
 *   - the slug is unknown
 *
 * The Skill dropdown also silently falls back to no preselection when
 * the returned value isn't present in the live facet, satisfying the
 * EDUN-12256 success criterion that missing or incomplete mappings
 * never produce errors or empty states.
 */
export const getSkillFromPath = (
  pathname: string | null | undefined,
): string | undefined => {
  if (!pathname) return undefined;

  const normalized = trimTrailingSlash(stripLocalePrefix(pathname));
  if (PATH_TO_SKILL.has(normalized)) {
    return PATH_TO_SKILL.get(normalized);
  }

  // Walk up extra segments so deeper Learn URLs (e.g.
  // /learn/python/some-subpath) still resolve to the parent topic skill.
  const segments = normalized.split("/").filter(Boolean);
  while (segments.length > 2) {
    segments.pop();
    const candidate = "/" + segments.join("/");
    if (PATH_TO_SKILL.has(candidate)) {
      return PATH_TO_SKILL.get(candidate);
    }
  }

  return undefined;
};
